import React, { Fragment } from 'react';
import { motion } from 'framer-motion';
import ReactHotkeys from 'react-hot-keys';
import { useDispatch } from 'react-redux';
import { toggleShowEkaCommDialog } from '../../store/actions/session';
import {
    EkaCareIcon,
    ekaCreditsIcon,
    secureIcon,
    smsIcon,
    validTickIcon,
    whatsAppIcon,
} from './Icons';
import Tag from './Tag';
import { isDoctorInternal, isDoctorNOSA } from '../../store/utils';
import { getDoc } from '../../store/idbHandler';
import { RupeeIcon } from './constants/khataUtils';

const ekaCommunications = [
    {
        title: 'Eka Patient App',
        description:
            'Your patients will receive a push notification on their app with the prescription.',
        icon: <EkaCareIcon />,
        cost: <p className="text-16 font-700 text-darwin-green-primary">Always Free</p>,
        tags: (
            <Fragment>
                <Tag
                    icon={
                        <span className="flex items-center justify-center p-4 rounded-full border border-accent-dark-green">
                            {validTickIcon('w-8 h-8 fill-accent-dark-green')}
                        </span>
                    }
                    color="green"
                    text="100% Delivery Rate"
                />
                <Tag icon={secureIcon()} text="100% Secure" color="green" />
                <Tag icon={<EkaCareIcon />} text="Needs Eka Patient App" color="blue" />
            </Fragment>
        ),
    },
    {
        title: 'SMS',
        description: 'Your patients will receive a SMS from Eka for every communication you send.',
        icon: smsIcon('w-16 h-16'),
        cost: (
            <div className="flex flex-col items-end gap-8">
                <p className="text-14 font-700">0.14 credits/message</p>
                <p className="text-13 font-400">1 credit = 1 Rupee</p>
            </div>
        ),
        tags: (
            <Fragment>
                <Tag
                    icon={
                        <span className="flex items-center justify-center p-4 rounded-full border border-neutral_new-1000">
                            {validTickIcon('w-8 h-8 fill-darwin-neutral_new-1000')}
                        </span>
                    }
                    color="yellow"
                    text="70% Delivery Rate"
                />
                <Tag
                    icon={<RupeeIcon className="fill-darwin-accent-bright-blue-800 w-14 h-14" />}
                    text="Cheap"
                    color="blue"
                />
                <Tag icon={ekaCreditsIcon('w-24 h-24')} text="Utilises Eka Credits" color="grey" />
            </Fragment>
        ),
    },
    {
        title: 'Whatsapp',
        description:
            'Your patients will receive a whatsapp from Eka for every communication you send.',
        icon: whatsAppIcon('w-20 h-20'),
        cost: (
            <div className="flex flex-col items-end gap-8">
                <p className="text-14 font-700">0.66 credits/message</p>
                <p className="text-13 font-400">1 credit = 1 Rupee</p>
            </div>
        ),
        tags: (
            <Fragment>
                <Tag
                    icon={
                        <span className="flex items-center justify-center p-4 rounded-full border border-accent-dark-green">
                            {validTickIcon('w-8 h-8 fill-accent-dark-green')}
                        </span>
                    }
                    color="green"
                    text="100% Delivery Rate"
                />
                <Tag icon={secureIcon()} text="100% Secure" color="green" />
                <Tag icon={ekaCreditsIcon('w-24 h-24')} text="Utilises Eka Credits" color="grey" />
            </Fragment>
        ),
    },
    {
        title: 'Own Whatsapp',
        description:
            'Your patients will receive a message from your own whatsapp for every communication you send.',
        icon: whatsAppIcon('w-20 h-20 fill-darwin-green-primary'),
        cost: <p className="text-16 font-700 text-darwin-green-primary">Always Free</p>,
        tags: (
            <Fragment>
                <Tag
                    icon={
                        <span className="flex items-center justify-center p-4 rounded-full border border-accent-dark-green">
                            {validTickIcon('w-8 h-8 fill-accent-dark-green')}
                        </span>
                    }
                    color="green"
                    text="100% Delivery Rate"
                />
                <Tag
                    icon={secureIcon('w-16 h-16 fill-darwin-neutral_new-1000')}
                    color="yellow"
                    text="Privacy Concerns"
                />
            </Fragment>
        ),
    },
];

const EkaCommunicationsDialog: React.FC = () => {
    const dispatch = useDispatch();

    const close = async () => {
        dispatch(toggleShowEkaCommDialog(false));
    };

    const doc = getDoc();

    const isPremium =
        doc?.profile.professional.saas && doc.profile.professional.saas_detail?.status
            ? true
            : false;

    return (
        <ReactHotkeys keyName="esc" onKeyDown={close}>
            <motion.div
                initial={{ opacity: 0 }}
                exit={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                style={{ zIndex: 9999 }}
                transition={{ duration: 0.2, ease: 'linear' }}
                className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 font-lato"
            >
                <div className="modal-overlay" onClick={close}></div>
                <div
                    className="modal-content w-60p flex flex-col"
                    style={{ maxHeight: 'calc(100vh - 125px)', background: 'transparent' }}
                >
                    <div className="bg-white rounded-t-8">
                        <p className="text-20 py-12 px-16 font-400">
                            Understand Eka's Communication Methods
                        </p>
                        {isDoctorNOSA(doc) || isDoctorInternal(doc)
                            ? null
                            : isPremium && (
                                  <div className="p-8 gap-8 bg-darwin-green-primary flex justify-center items-center w-full">
                                      <svg
                                          width="21"
                                          height="20"
                                          viewBox="0 0 21 20"
                                          fill="none"
                                          xmlns="http://www.w3.org/2000/svg"
                                      >
                                          <mask
                                              id="mask0_603_2853"
                                              // style="mask-type:alpha"
                                              style={{ maskType: 'alpha' }}
                                              maskUnits="userSpaceOnUse"
                                              x="1"
                                              y="0"
                                              width="19"
                                              height="20"
                                          >
                                              <path
                                                  d="M19.9546 9.99996C19.9546 15.2216 15.7216 19.4545 10.5 19.4545C5.27841 19.4545 1.04547 15.2216 1.04547 9.99996C1.04547 4.77834 5.27841 0.54541 10.5 0.54541C15.7216 0.54541 19.9546 4.77834 19.9546 9.99996Z"
                                                  fill="white"
                                              />
                                          </mask>
                                          <g mask="url(#mask0_603_2853)">
                                              <path
                                                  d="M22.3182 11.1818C22.3182 16.4035 18.0853 20.6364 12.8637 20.6364C7.64205 20.6364 3.40912 16.4035 3.40912 11.1818C3.40912 5.96023 7.64205 1.72729 12.8637 1.72729C18.0853 1.72729 22.3182 5.96023 22.3182 11.1818Z"
                                                  fill="url(#paint0_linear_603_2853)"
                                              />
                                          </g>
                                          <path
                                              fill-rule="evenodd"
                                              clip-rule="evenodd"
                                              d="M10.5 1.50526C5.80852 1.50526 2.00532 5.30846 2.00532 9.99996C2.00532 14.6915 5.80852 18.4946 10.5 18.4946C14.3116 18.4946 17.538 15.9839 18.6133 12.5249C18.7576 12.0568 18.8047 11.7392 18.7983 11.535C18.7951 11.4357 18.7797 11.3786 18.7677 11.3495C18.762 11.3356 18.7573 11.3285 18.7551 11.3256L18.7528 11.3228L18.7514 11.3215C18.7514 11.3215 18.6992 11.2828 18.5086 11.3017C18.3248 11.3199 18.0806 11.3853 17.7765 11.5C17.1687 11.7292 16.4333 12.1107 15.6507 12.5178L15.6296 12.5288C14.869 12.9245 14.067 13.3417 13.3608 13.615C12.4747 13.9581 11.5573 13.8424 10.8242 13.499C10.103 13.1612 9.49378 12.5699 9.27826 11.8769L9.27536 11.8676L9.27285 11.8581C9.23264 11.7076 9.19425 11.5664 9.15716 11.4346C8.93523 11.4106 8.70423 11.3577 8.46501 11.2746L8.44904 11.269L8.43352 11.2624C8.19226 11.1589 7.80593 10.9577 7.58844 10.6457C7.46738 10.472 7.37527 10.2279 7.44091 9.94658C7.50323 9.67935 7.68665 9.4732 7.90345 9.31491L7.92765 9.29723L7.95389 9.28268C8.22394 9.13303 8.53285 9.05029 8.84999 9.1443C9.15793 9.23556 9.36729 9.46298 9.51327 9.69259C9.63811 9.88901 9.74709 10.1375 9.84781 10.4212C9.8676 10.4143 9.8869 10.4069 9.90573 10.3991C10.181 10.2851 10.4152 10.0608 10.601 9.74372C10.9106 9.21542 11.0393 8.50594 11.0096 7.91961H8.42635C8.16127 7.91961 7.9464 7.70474 7.9464 7.43966C7.9464 7.17463 8.16127 6.95976 8.42635 6.95976H10.7728C10.6788 6.76665 10.5619 6.60307 10.4303 6.47966H8.16463C7.89955 6.47966 7.68468 6.26479 7.68468 5.99972C7.68468 5.73468 7.89955 5.51981 8.16463 5.51981H13.039C13.304 5.51981 13.5189 5.73468 13.5189 5.99972C13.5189 6.26479 13.304 6.47966 13.039 6.47966H11.6057C11.6849 6.6336 11.7509 6.79502 11.8041 6.95976H13.0391C13.3042 6.95976 13.5191 7.17463 13.5191 7.43966C13.5191 7.70474 13.3042 7.91961 13.0391 7.91961H11.9702C11.9993 8.66814 11.8329 9.54004 11.4291 10.229C11.1677 10.6752 10.7901 11.0716 10.2731 11.2858C10.2253 11.3057 10.1766 11.3238 10.1273 11.3401C10.1507 11.4257 10.174 11.5125 10.1975 11.6002C10.3147 11.9652 10.6856 12.3741 11.2314 12.6298C11.7695 12.8819 12.4127 12.9528 13.0143 12.7199C13.6673 12.4672 14.4264 12.0727 15.2077 11.6663L15.2226 11.6586C15.9836 11.2627 16.7693 10.854 17.4378 10.6019C17.7741 10.4751 18.1087 10.3768 18.4139 10.3465C18.7125 10.317 19.06 10.3452 19.3413 10.5644C19.6356 10.7936 19.7461 11.1425 19.7576 11.5046C19.769 11.8625 19.688 12.297 19.5303 12.8085C18.3339 16.6576 14.744 19.4545 10.5 19.4545C5.27841 19.4545 1.04547 15.2216 1.04547 9.99996C1.04547 4.77835 5.27841 0.54541 10.5 0.54541C15.1144 0.54541 18.9517 3.88662 19.7862 8.25486C19.8359 8.51522 19.6514 8.75808 19.3888 8.79452C19.1263 8.83097 18.8853 8.64706 18.8341 8.38701C18.0647 4.48133 14.623 1.50526 10.5 1.50526ZM8.79364 10.3723C8.76378 10.3104 8.73375 10.2555 8.70323 10.2075C8.62945 10.0915 8.58429 10.0678 8.57739 10.0647C8.56612 10.064 8.5268 10.0667 8.44568 10.1081C8.43198 10.1189 8.42063 10.1285 8.41122 10.1371C8.43788 10.1632 8.47732 10.1957 8.53247 10.2323C8.61181 10.2849 8.704 10.3329 8.79364 10.3723Z"
                                              fill="#1A1A1A"
                                          />
                                          <defs>
                                              <linearGradient
                                                  id="paint0_linear_603_2853"
                                                  x1="4.08444"
                                                  y1="4.09093"
                                                  x2="24.7488"
                                                  y2="8.53485"
                                                  gradientUnits="userSpaceOnUse"
                                              >
                                                  <stop stop-color="#CCA557" />
                                                  <stop offset="0.505208" stop-color="#FFE5AE" />
                                                  <stop offset="1" stop-color="#CCA557" />
                                              </linearGradient>
                                          </defs>
                                      </svg>

                                      <p className="text-14 font-400 text-white">
                                          Since you're a{' '}
                                          {doc?.profile.professional.saas === 'APT'
                                              ? 'PLUS'
                                              : 'PRO'}{' '}
                                          user, you get{' '}
                                          <span className="font-700">
                                              100 FREE CREDITS every month!
                                          </span>
                                      </p>
                                  </div>
                              )}
                    </div>
                    <div className="flex overflow-auto hide-scrollbar w-full flex-col p-16 gap-16 bg-darwin-neutral_new-50">
                        {ekaCommunications.map((e) => (
                            <div className="flex gap-16 p-24 w-full bg-white rounded-8">
                                <span className="flex self-start items-center justify-center bg-darwin-neutral_new-50 py-12 px-13 rounded-full">
                                    {e.icon}
                                </span>
                                <div className="flex flex-col items-start gap-8">
                                    <div className="flex flex-col items-start">
                                        <p className="text-16 font-700 whitespace-nowrap">
                                            {e.title}
                                        </p>
                                        <p className="text-14 font-400">{e.description}</p>
                                    </div>
                                    <div className="flex w-full item-center flex-wrap gap-8">
                                        {e.tags}
                                    </div>
                                </div>
                                <div className="flex flex-col h-full gap-8 ml-auto whitespace-nowrap">
                                    {e.cost}
                                    {e.title === 'SMS' && (
                                        <div className="mt-auto flex flex-col items-end">
                                            <p className="text-13 text-darwin-neutral-600 font-400">
                                                SMS provider charges:{' '}
                                                <span className="font-700">₹0.14</span>
                                            </p>
                                        </div>
                                    )}
                                    {e.title === 'Whatsapp' && (
                                        <div className="mt-auto flex flex-col gap-2 items-end">
                                            <p className="text-13 text-darwin-neutral-600 font-400">
                                                WhatsApp charges:{' '}
                                                <span className="font-700">₹0.84</span>
                                            </p>
                                            <p className="text-13 text-darwin-green-primary font-400">
                                                Eka discount:{' '}
                                                <span className="font-700">₹0.18</span>
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="w-full rounded-b-8 flex border-t border-darwin-neutral_new-200 flex-col items-center">
                        <div
                            className={`flex justify-between px-16 rounded-b-8 py-12 items-center bg-white w-full`}
                        >
                            <div
                                onClick={close}
                                className="cursor-pointer flex rounded-8 font-700 px-12 py-6 justify-center items-center bg-white border-darwin-accent-bright-blue-800 text-darwin-accent-bright-blue-800"
                            >
                                Cancel
                            </div>
                            <div
                                onClick={close}
                                className="cursor-pointer flex rounded-8 font-700 px-12 py-6 justify-center items-center bg-white bg-darwin-accent-bright-blue-800 text-white"
                            >
                                Ok, Got it!
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>
        </ReactHotkeys>
    );
};

export default EkaCommunicationsDialog;
